import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, useState, type ComponentProps } from "react";
import { assets } from "@core/assets";
import { cn } from "@core/utils/cn";

/* ==================== TEXTAREA STYLES ==================== */
const textareaStyles = cva(
  [
    "relative",
    "py-1.5",
    "px-2.5",
    "ease-in-out",
    "duration-200",
    "transition-all",
    "w-full",
    "appearance-none",
    "rounded-lg",
    "text-sm",
    "outline-none",
    // Focus ring — primary border
    "focus-visible:ring-0",
    "focus-visible:border-primary-800",
    // Disabled
    "disabled:cursor-not-allowed",
    "disabled:bg-neutral-50",
    "disabled:text-neutral-300",
    "disabled:placeholder:text-neutral-300",
    "disabled:border-neutral-200",
  ],
  {
    variants: {
      variant: {
        primary: [
          "border-[1px]",
          "bg-white",
          "placeholder:text-neutral-300",
          "hover:bg-neutral-50",
          "hover:border-neutral-200",
        ],
      },
      state: {
        default: ["border-neutral-200", "text-neutral-950"],
        error: [
          "border-accent-red-800",
          "bg-neutral-50",
          "focus-visible:border-accent-red-800",
        ],
        info: [
          "border-primary-800",
          "text-primary-800",
          "placeholder:text-primary-800",
          "focus-visible:border-primary-800",
        ],
        warning: [
          "border-accent-yellow-700",
          "text-accent-yellow-800",
          "placeholder:text-accent-yellow-400",
          "focus-visible:border-accent-yellow-700",
        ],
        success: [
          "border-accent-green-700",
          "text-accent-green-700",
          "placeholder:text-accent-green-400",
          "focus-visible:border-accent-green-700",
        ],
      },
    },
    defaultVariants: {
      variant: "primary",
      state: "default",
    },
  },
);

/* ==================== WRAPPER ==================== */
const wrapperVariants = cva("relative w-full", {
  variants: {
    wrapperVariant: {
      default: "flex flex-col",
      flexed: "flex items-center gap-3",
    },
  },
  defaultVariants: {
    wrapperVariant: "default",
  },
});

/* ==================== LABEL ==================== */
const labelStyles = cva(
  [
    "block",
    "mb-1",
    "text-sm",
    "font-medium",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "select-none",
  ],
  {
    variants: {
      state: {
        default: "text-neutral-950",
        error: "text-accent-red-800",
        focused: "text-primary-800",
      },
      required: {
        true: "after:content-['*'] after:ml-1 after:text-accent-red-800",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

/* ==================== HELPER TEXT ==================== */
const helperTextStyles = cva(
  ["flex", "mt-2", "text-sm", "gap-2", "items-center"],
  {
    variants: {
      state: {
        default: "text-neutral-950",
        info: "text-primary-800",
        error: "text-accent-red-600",
        warning: "text-accent-yellow-700",
        success: "text-accent-green-700",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

/* ==================== TYPES ==================== */
type TextAreaProps = ComponentProps<"textarea"> &
  VariantProps<typeof textareaStyles> &
  VariantProps<typeof wrapperVariants> & {
    label?: string;
    required?: boolean;
    helperText?: string;
  };

/* ==================== COMPONENT ==================== */
const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      variant,
      wrapperVariant,
      state = "default",
      className,
      label,
      required = false,
      helperText,
      id,
      ...props
    },
    ref,
  ) => {
    const helperId = id ? `${id}-helper` : undefined;
    const [isFocused, setIsFocused] = useState(false);

    const getLabelState = () => {
      if (state === "error") return "error";
      if (isFocused) return "focused";
      return "default";
    };

    const getStateIcon = () => {
      switch (state) {
        case "info":
          return assets["circle-alert"];
        case "success":
          return assets["circle-check-big"];
        case "warning":
          return assets["triangle-alert"];
        case "error":
          return assets["circle-x"];
        default:
          return assets["circle-alert"];
      }
    };

    const StateIcon = getStateIcon();
    const helperState =
      state === "default"
        ? "default"
        : (state as "info" | "error" | "warning" | "success");

    return (
      <div className={cn(wrapperVariants({ wrapperVariant }))}>
        {label && (
          <h4
            className={cn(
              labelStyles({ state: getLabelState(), required }),
              "flex items-center",
              wrapperVariant === "flexed" ? "min-w-[160px]" : "",
            )}
          >
            <span className="text-sm">{label}</span>
          </h4>
        )}

        <textarea
          ref={ref}
          id={id}
          aria-describedby={helperText && helperId ? helperId : undefined}
          className={cn(
            textareaStyles({ variant, state }),
            "min-h-[6rem] resize-y",
            className,
          )}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...props}
        />

        {helperText && (
          <div className={cn(helperTextStyles({ state: helperState }))}>
            <StateIcon className="text-lg flex-shrink-0" />
            <span id={helperId}>{helperText}</span>
          </div>
        )}
      </div>
    );
  },
);

TextArea.displayName = "TextArea";
export default TextArea;
